<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <div class="info-content-container flex-column">
        <span class="sac__font-14 med">Step 2 of 3</span>
        <span class="sac__font-18 bold"> Membership Plans Mapping </span>
        <p class="sac__font-14 mt-2">
          Map your membership plans in ServiceTitan to SmartMaintenance plans within SmartAC.com.
        </p>
      </div>
      <button type="button" class="ac-close-button" @click="handleNext">
        <img src="@shared/assets/img/general/x-black.png" alt="x" class="ac-close" />
      </button>
    </div>
    <div class="ac-body-container">
      <div class="map-item-container">
        <div class="sac__font-16 semi-bold">ServiceTitan Membership Type</div>
        <span style="width: 30px" />
        <div class="sac__font-16 semi-bold">SmartMaintenance Plans</div>
      </div>
      <div v-for="(item, idx) in membershipTypes" :key="idx" class="map-item-container">
        <span class="sac__font-16 n50">{{ item.name }}</span>
        <img src="@shared/assets/img/general/mapping-icon.png" alt="arrows" class="img-size-24" />
        <SACDropdown
          :field="`${item.id}`"
          :change="handleChange"
          :value="mappings.find((x) => x.externalId === item.id)?.internalId"
          :options="planOptions"
          :error="mappings.find((x) => x.externalId === item.id)?.error"
        />
      </div>
    </div>
    <div class="ac-footer-container">
      <button type="button" class="info-form-button-outline mr-2" @click="handleNext">
        Cancel
      </button>
      <button
        :disabled="updating || isNextDisabled"
        type="button"
        class="info-form-button"
        @click="submit"
      >
        Next
      </button>
    </div>
    <ProcessOverlay :show="updating || loadingMappings" :text="loadingText" />
  </div>
</template>

<script>
import customerDetailMixin from '@shared/mixins/customerDetailMixin';
import SACDropdown from '@shared/components/sac/SACDropdown.vue';
import { mapState } from 'vuex';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';

export default {
  name: 'MapMembershipTypesTitanPopup',
  components: {
    ProcessOverlay,
    SACDropdown,
  },
  mixins: [customerDetailMixin],
  props: {
    toggle: Function,
  },
  data() {
    return {
      mappings: [],
      updating: false,
      loadingText: 'Saving..',
    };
  },
  computed: {
    ...mapState({
      membershipMappings: (state) => state.serviceTitan.membershipMappings,
      membershipTypes: (state) => state.serviceTitan.membershipTypes,
      stTenantId: (state) => state.setting.partnerSettings?.ST_TENANT_ID,
      existingMembershipMapping: (state) => state.serviceTitan.existingMembershipMapping,
      loadingMappings: (state) => state.serviceTitan.loadingMappings,
      backScreen: (state) => state.serviceTitan.backScreen,
      currentAdmin: (state) => state.admin.current,
      allCorpPlansWithArchived: (state) => state.customerDetail.allCorpPlansWithArchived,
    }),
    planOptions() {
      return [
        { label: 'Not mapped', value: -1 },
        ...this.allCorpPlansWithArchived.map((plan) => ({
          label: plan.name,
          value: plan.planId,
        })),
      ];
    },
    isNextDisabled() {
      return this.mappings.find((x) => x.error);
    },
  },
  watch: {
    membershipMappings: {
      handler(newVal, oldVal) {
        this.mappings =
          Array.isArray(newVal) && newVal.length
            ? newVal.map((mapping) => ({
                ...mapping,
                internalId: mapping.internalId,
                externalId: Number(mapping.externalId),
              }))
            : [];
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.initialLoad();
  },
  methods: {
    initialLoad() {
      this.loadingText = 'Loading..';
      if (this.allCorpPlansWithArchived?.length === 0) {
        this.$store.dispatch('getAllCorpPlansWithArchived', {
          corpId: this.currentAdmin.corporationId,
        });
      }
      if (this.membershipTypes?.length === 0) {
        this.$store.dispatch('getMembershipTypePlans');
      }
      if (this.membershipMappings?.length === 0) {
        this.$store.dispatch('queryMappingsByCorpId');
      }
      if (this.stTenantId) {
        this.$store.dispatch('getMembershipTypeMappingsByTenant', this.stTenantId);
      }
    },
    handleNext() {
      if (this.backScreen === 'review') {
        this.toggle(true, 'titan-edit-integration');
      } else {
        this.toggle(true, 'titan-map-jobs');
      }
    },
    handleChange(externalId, internalId) {
      const mapping = this.mappings.find((m) => m.externalId === Number(externalId));
      const existingMapping = this.existingMembershipMapping.find(
        (e) =>
          e.externalId === externalId &&
          e.mapType === 'MembershipType' &&
          Number(internalId) !== -1 &&
          this.currentAdmin.corporationId !== e.corpId,
      );
      if (mapping) {
        mapping.internalId = internalId;
        mapping.error = existingMapping ? 'Membership plan is already mapped' : null;
      } else {
        this.addMapping(externalId, internalId, !!existingMapping);
      }
    },
    addMapping(externalId, internalId, error) {
      this.mappings.push({
        externalId: Number(externalId),
        internalId,
        mapType: 'MembershipType',
        externalSource: 'Service Titan',
        externalSystemId: this.stTenantId || null,
        error: error ? 'Membership plan is already mapped' : null,
      });
    },
    async submit() {
      try {
        this.loadingText = 'Saving..';
        this.updating = true;
        await this.$store.dispatch('setMembershipTypeMappings', this.mappings);
        this.handleNext();
      } finally {
        this.updating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ac-card-content {
  .ac-header-container {
    display: flex;
    justify-content: space-between;
    .info-content-container {
      align-items: flex-start;
      display: inline-flex;
      gap: 8px;
      width: 100%;
    }
  }
  .map-item-container {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 1rem;
    align-items: center;
    min-height: 3rem;
    justify-content: center;
  }
  .loading-img {
    margin-top: 45px;
    margin-bottom: 45px;
    height: 45px;
  }
  .info-form-button {
    padding: 12px;
    height: 48px;
    border-radius: 6px;
    background-color: $NBLUE100;
    font-weight: 600;
    color: $WHITE;
    width: 155px;
    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
  .info-form-button-outline {
    padding: 12px;
    height: 48px;
    border-radius: 4px;
    border: 2px solid $NBLUE100;
    color: $NBLUE100;
    font-weight: 600;
    width: 155px;
  }
}
</style>
